import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, from, throwError } from 'rxjs';
import { switchMap, map, catchError } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { UtilService } from '../util/util.service';
import { Auth } from 'aws-amplify';

export interface ImageUploadResult {
    Location: string;
    publicUrl: string;
    key: string;
    bucket: string;
}

@Injectable({
    providedIn: 'root',
})
export class ImageUploadService {
    private readonly API_BASE = environment.baseUrl2;
    private readonly MAX_BYTES = 10 * 1024 * 1024; // 10MB client cap

    private readonly ALLOWED_FILE_TYPES = new Set(['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/heic', 'image/heif']);

    constructor(
        private http: HttpClient,
        private utility: UtilService
    ) {}

    validate(file: File | Blob, fileName = 'photo.jpg'): string | null {
        if (!file) return 'No file selected';

        const size = file.size || 0;
        if (size <= 0) return 'File is empty';
        if (size > this.MAX_BYTES) {
            return 'Image must be under 10 MB. Please choose a smaller image.'
        }

        const type = (file.type || '').toLowerCase();
        if (!this.ALLOWED_FILE_TYPES.has(type)) {
            return 'Only JPG, PNG and WebP images are allowed.';
        }

        if (!fileName || !fileName.includes('.')) {
            return 'Invalid file name.';
        }

        return null;
    }

    upload(file: File | Blob, location: string, fileName = 'photo.jpg'): Observable<ImageUploadResult> {
        const validationError = this.validate(file, fileName);
        if (validationError) {
          return throwError(() => ({ error: { message: validationError } }));
        }

        const contentType = (file.type || 'image/jpeg').toLowerCase();

        return this.requestPresignedUrl({
          location,
          fileName,
          contentType,
          size: file.size,
        }).pipe(
          switchMap((presign) => from(this.putToS3(presign.uploadUrl, file, contentType)).pipe(
            // Screen the photo before anyone gets the URL — a rejected one is
            // deleted server-side, so it must never reach a comment or message.
            switchMap(() => this.moderate(presign.key)),
            map(() => ({
              Location: presign.publicUrl,
              publicUrl: presign.publicUrl,
              key: presign.key,
              bucket: presign.bucket,
            })),
          )),
        );
      }

    private requestPresignedUrl(body: {
        location: string;
        fileName: string;
        contentType: string;
        size: number;
    }): Observable<any> {
        return from(this.buildHeaders()).pipe(
          switchMap((headers) =>
            this.http.post<any>(`${this.API_BASE}file/presign`, body, { headers })
          ),
        );
    }

    private moderate(key: string): Observable<any> {
        return from(this.buildHeaders()).pipe(
          switchMap((headers) =>
            this.http.post<any>(
              `${this.API_BASE}file/moderate`,
              { key },
              { headers },
            )
          ),
          // Every photo upload passes through here, so the refusal is shown
          // once rather than in each page that attaches an image.
          catchError((error) => {
            if (error?.error?.error === 'ERR_IMAGE_BLOCKED') {
              this.utility.presentBlockedToast(
                error.error.title || "Photo couldn't be uploaded",
                error.error.message
              );
            }
            return throwError(() => error);
          }),
        );
    }

    private async putToS3(uploadUrl: string, file: File | Blob, contentType: string): Promise<void> {
        const response = await fetch(uploadUrl, {
          method: 'PUT',
          body: file,
          headers: {
            'Content-Type': contentType,
          },
        });

        if (!response.ok) {
          const text = await response.text().catch(() => '');
          throw new Error(text || `S3 upload failed with status ${response.status}`);
        }
    }

    private async buildHeaders(): Promise<HttpHeaders> {
        let headers = new HttpHeaders();
        try {
          const session = await Auth.currentSession();
          const token = session.getIdToken().getJwtToken();
          headers = headers.set('Authorization', `Bearer ${token}`);
        } catch (e) {
          console.error('image-upload buildHeaders failed', e);
        }
        return headers;
    }
}